import { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
    Box,
    Link,
    styled,
} from '@mui/material';

interface TermsAndConditionsModalProps {
    open: boolean;
    onAccept: (openDatasets: boolean) => void;
}

export const TermsAndConditionsModal = ({ open, onAccept }: TermsAndConditionsModalProps) => {
    const [openDatasets, setOpenDatasets] = useState<boolean>(false);
    const [isAccepting, setIsAccepting] = useState<boolean>(false);

    const handleAccept = async () => {
        setIsAccepting(true);
        try {
            await onAccept(openDatasets);
        } catch (error) {
            console.error('Error accepting terms:', error);
            setIsAccepting(false);
        }
    };

    return (
        <StyledDialog
            open={open}
            disableEscapeKeyDown
            onClose={(_event, reason) => {
                // Prevent closing by clicking outside or pressing ESC
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
            }}
            maxWidth="md"
            fullWidth
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(10, 50, 53, 0.4)', // Semi-transparent dark teal
                    backdropFilter: 'blur(4px)', // Blur the background app
                },
            }}
        >
            <DialogTitle sx={{ bgcolor: 'rgb(250, 242, 233)', borderBottom: '1px solid rgba(10, 50, 53, 0.1)' }}>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: '#0A3235' }}>
                    Terms of Use & Data Consent
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ bgcolor: 'rgb(250, 242, 233)', borderColor: 'rgba(10, 50, 53, 0.1)' }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" paragraph sx={{ color: '#2D2D2D' }}>
                        By accepting these terms and using Paper+Figure QA, you confirm that you have read
                        and agree to Ai2's full{' '}
                        <Link
                            href="https://allenai.org/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: '#0FCB8C', fontWeight: 600, textDecoration: 'underline' }}
                        >
                            Terms of Use
                        </Link>{' '}
                        and{' '}
                        <Link
                            href="https://allenai.org/responsible-use"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: '#0FCB8C', fontWeight: 600, textDecoration: 'underline' }}
                        >
                            Responsible Use Guidelines
                        </Link>{' '}
                        and acknowledge Ai2's{' '}
                        <Link
                            href="https://allenai.org/privacy-policy.html"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: '#0FCB8C', fontWeight: 600, textDecoration: 'underline' }}
                        >
                            Privacy Policy
                        </Link>
                        .
                    </Typography>

                    <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                        <Typography component="li" variant="body1" sx={{ mb: 1, color: '#2D2D2D' }}>
                            You agree not to submit any personal, sensitive, proprietary, or confidential
                            information in your queries.
                        </Typography>
                        <Typography component="li" variant="body1" sx={{ mb: 1, color: '#2D2D2D' }}>
                            You agree that Ai2 may use your queries for AI training and scientific research.
                        </Typography>
                        <Typography component="li" variant="body1" sx={{ color: '#2D2D2D' }}>
                            Our protocol has been reviewed and found exempt by the University of Washington{' '}
                            <Link
                                href="https://www.washington.edu/research/hsd/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: '#0FCB8C', fontWeight: 600, textDecoration: 'underline' }}
                            >
                                IRB
                            </Link>
                            .
                        </Typography>
                    </Box>
                </Box>

                <OptionalConsentBox>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 'bold', color: '#0A3235', mb: 1.5 }}
                    >
                        Optional Consent for Open Science
                    </Typography>
                    <Typography variant="body2" paragraph sx={{ color: '#3D3D3D' }} id="open-datasets-intro">
                        To use Paper+Figure QA you only need to agree to the terms above. To help us improve
                        this technology and accelerate scientific discovery, please consider granting this
                        optional permission:
                    </Typography>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={openDatasets}
                                onChange={(e) => setOpenDatasets(e.target.checked)}
                                slotProps={{
                                    input: {
                                        'aria-label': 'Consent for the inclusion of Paper+Figure QA data in open datasets',
                                        'aria-describedby': 'open-datasets-intro open-datasets-description',
                                    }
                                }}
                                sx={{
                                    color: '#0FCB8C',
                                    '&.Mui-checked': {
                                        color: '#0FCB8C',
                                    },
                                }}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#2D2D2D' }}>
                                    Help improve open science!
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#3D3D3D' }} id="open-datasets-description">
                                    I consent to the inclusion of my de-identified interactions (queries &
                                    responses) with Paper+Figure QA in a public research dataset.
                                </Typography>
                            </Box>
                        }
                    />
                </OptionalConsentBox>
            </DialogContent>

            <DialogActions sx={{ p: 3, justifyContent: 'center', bgcolor: 'rgb(250, 242, 233)' }}>
                <AcceptButton
                    onClick={handleAccept}
                    disabled={isAccepting}
                    variant="contained"
                    size="large"
                    aria-label={'Agree to terms and use Paper+Figure QA'}
                >
                    {isAccepting ? 'Processing...' : 'Agree & Use Paper+Figure QA'}
                </AcceptButton>
            </DialogActions>
        </StyledDialog>
    );
};

const StyledDialog = styled(Dialog)`
    .MuiDialog-paper {
        border-radius: 12px;
        background-color: rgb(250, 242, 233);
    }
`;

const OptionalConsentBox = styled(Box)`
    background-color: rgba(255, 255, 255, 0.6);
    border-left: 4px solid #0FCB8C;
    padding: ${({ theme }) => theme.spacing(2.5)};
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const AcceptButton = styled(Button)`
    && {
        background-color: #0FCB8C;
        color: #0A3235;
        font-weight: bold;
        padding: ${({ theme }) => theme.spacing(1.5, 4)};
        font-size: 1rem;
        text-transform: none;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(15, 203, 140, 0.2);

        &:hover {
            background-color: #0DB87D;
            box-shadow: 0 4px 8px rgba(15, 203, 140, 0.3);
        }

        &:disabled {
            background-color: #D1D5DB;
            color: #6B7280;
            box-shadow: none;
        }
    }
`;
